/**
 * Forgiving project search, shared by the Site Plans grid and the share-link
 * project picker. Tolerates
 * typos ("amberwod"), missing spaces or punctuation ("8bt" → "8@BT",
 * "26newton"), partial words ("thom res") and any word order. Results are
 * ranked: name matches first, then district / area / address.
 */

export interface SearchableProject {
  name: string;
  district: string | null;
  area: string | null;
  address: string | null;
}

const words = (s: string): string[] => s.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
const compact = (s: string): string => words(s).join('');

/** Levenshtein distance, giving up (returns max + 1) once it exceeds `max`. */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      rowMin = Math.min(rowMin, cur[j]);
    }
    if (rowMin > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

/** Typos allowed for a query word of this length. */
const typoBudget = (len: number): number => (len >= 7 ? 2 : len >= 4 ? 1 : 0);

/** How well one query word matches one field; 0 = no match. */
function scoreToken(token: string, fieldWords: string[], fieldCompact: string): number {
  let best = 0;
  for (const w of fieldWords) {
    if (w === token) return 4;
    if (w.startsWith(token)) best = Math.max(best, 3);
    else if (w.includes(token)) best = Math.max(best, 2);
    else {
      const budget = typoBudget(token.length);
      // Compare against the word, or its start when the user is still typing.
      if (budget > 0 && Math.min(
        editDistance(token, w, budget),
        editDistance(token, w.slice(0, token.length), budget),
      ) <= budget) best = Math.max(best, 1.5);
    }
  }
  if (!best && fieldCompact.includes(token)) best = 2;
  return best;
}

/** 0 = no match; higher is better. */
export function scoreProject(project: SearchableProject, query: string): number {
  const qWords = words(query);
  if (qWords.length === 0) return 1;
  const fields = [
    { text: project.name, weight: 3 },
    { text: project.district ? `d${project.district} district ${project.district}` : '', weight: 1 },
    { text: project.area || '', weight: 1 },
    { text: project.address || '', weight: 1 },
  ].map((f) => ({ ...f, words: words(f.text), compact: compact(f.text) }));

  let total = 0;
  for (const token of qWords) {
    const best = Math.max(...fields.map((f) => scoreToken(token, f.words, f.compact) * f.weight));
    if (best === 0) {
      // Fall back to the whole query run together ("26 newt on", "8 bt").
      const nameCompact = fields[0].compact;
      const q = qWords.join('');
      return nameCompact.includes(q) ? 6 : 0;
    }
    total += best;
  }
  // Whole query as one run inside the name: a strong signal.
  if (fields[0].compact.includes(qWords.join(''))) total += 6;
  return total;
}

/**
 * Matching projects, best first; input order breaks ties. Pass `toSearchable`
 * when the items don't already have `name` / `district` / `area` / `address`.
 */
export function searchProjects<T>(
  projects: T[],
  query: string,
  toSearchable: (item: T) => SearchableProject = (item) => item as unknown as SearchableProject,
): T[] {
  if (!query.trim()) return projects;
  return projects
    .map((p, i) => ({ p, i, score: scoreProject(toSearchable(p), query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((r) => r.p);
}
